import React, { useEffect, useRef } from 'react';

/**
 * Kurze Feuerwerk-/Konfetti-Animation für den Erfolgsmoment der
 * Schnellerfassung (Gamification). Bewusst dependency-frei: ein einzelnes
 * <canvas> über dem ganzen Viewport, das sich nach ~1.8s selbst beendet.
 *
 * Respektiert prefers-reduced-motion — dann rendert die Komponente nichts und
 * meldet sofort onDone(), damit der Erfolgs-State trotzdem korrekt weiterläuft.
 */

interface CelebrationProps {
  /** Steuert, ob die Animation läuft. Bei true startet ein neuer Durchlauf. */
  active: boolean;
  /** Wird nach Ende der Animation (oder sofort bei reduced-motion) aufgerufen. */
  onDone?: () => void;
}

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  life: number;     // verbleibende Lebensdauer (Frames)
  maxLife: number;
  color: string;
  size: number;
}

const COLORS = ['#0891B2', '#22D3EE', '#059669', '#34D399', '#FBBF24', '#F472B6', '#A78BFA'];

const prefersReducedMotion = (): boolean =>
  typeof window !== 'undefined' &&
  window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

const Celebration: React.FC<CelebrationProps> = ({ active, onDone }) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const rafRef = useRef<number | null>(null);
  const onDoneRef = useRef(onDone);
  onDoneRef.current = onDone;

  useEffect(() => {
    if (!active) return;

    if (prefersReducedMotion()) {
      onDoneRef.current?.();
      return;
    }

    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) {
      onDoneRef.current?.();
      return;
    }

    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const W = window.innerWidth;
    const H = window.innerHeight;
    canvas.width = W * dpr;
    canvas.height = H * dpr;
    ctx.scale(dpr, dpr);

    const particles: Particle[] = [];

    // Mehrere Explosionen, leicht zeitversetzt, im oberen Drittel verteilt.
    const burst = (cx: number, cy: number) => {
      const count = 46;
      for (let i = 0; i < count; i++) {
        const angle = (Math.PI * 2 * i) / count + Math.random() * 0.3;
        const speed = 2 + Math.random() * 4.5;
        const maxLife = 55 + Math.random() * 25;
        particles.push({
          x: cx,
          y: cy,
          vx: Math.cos(angle) * speed,
          vy: Math.sin(angle) * speed,
          life: maxLife,
          maxLife,
          color: COLORS[(Math.random() * COLORS.length) | 0],
          size: 2 + Math.random() * 2.5,
        });
      }
    };

    const bursts = [
      { t: 0, x: W * 0.5, y: H * 0.32 },
      { t: 220, x: W * 0.28, y: H * 0.24 },
      { t: 420, x: W * 0.72, y: H * 0.26 },
    ];
    const start = performance.now();
    let stopped = false;

    const tick = (now: number) => {
      const elapsed = now - start;
      bursts.forEach((b) => {
        if (!('fired' in b) && elapsed >= b.t) {
          (b as any).fired = true;
          burst(b.x, b.y);
        }
      });

      ctx.clearRect(0, 0, W, H);

      for (let i = particles.length - 1; i >= 0; i--) {
        const p = particles[i];
        p.vy += 0.06;          // Gravitation
        p.vx *= 0.99;          // Luftwiderstand
        p.x += p.vx;
        p.y += p.vy;
        p.life -= 1;
        if (p.life <= 0) {
          particles.splice(i, 1);
          continue;
        }
        const alpha = Math.max(0, p.life / p.maxLife);
        ctx.globalAlpha = alpha;
        ctx.fillStyle = p.color;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.globalAlpha = 1;

      const allFired = bursts.every((b) => (b as any).fired);
      if (!stopped && (particles.length > 0 || !allFired)) {
        rafRef.current = requestAnimationFrame(tick);
      } else {
        ctx.clearRect(0, 0, W, H);
        onDoneRef.current?.();
      }
    };

    rafRef.current = requestAnimationFrame(tick);

    return () => {
      stopped = true;
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [active]);

  if (!active) return null;

  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      className="pointer-events-none fixed inset-0 z-[60]"
    />
  );
};

export default Celebration;
